import { useCallback, useEffect, useRef, useState } from "react";

import { useInstrumentSetup } from "../contexts/InstrumentSetupContext";
import {
  marketDataOhlcGateFromSetupScan,
  type MarketDataConnectGateState,
} from "../utils/marketDataConnectGate";

export type UseWizardMarketDataOhlcGateOptions = {
  brokerAccountCode: string;
  /** Scope symbol; empty uses single-symbol account fallback from scan. */
  symbol?: string | null;
  /** When false, clears gate. Default true. */
  enabled?: boolean;
};

/**
 * Layer B OHLC gate for Trade Review / report / simulator.
 *
 * Does **not** call GET/POST instrument-setup. That endpoint is only for the
 * setup wizard (open / Refresh sources). Here we derive the connect notice from
 * cached wizard `setupData` when present; otherwise candles/resolve 422s cover
 * disconnected sources.
 */
export function useWizardMarketDataOhlcGate({
  brokerAccountCode,
  symbol,
  enabled = true,
}: UseWizardMarketDataOhlcGateOptions) {
  const { openSetup, isOpen: instrumentSetupOpen, setupData } = useInstrumentSetup();
  const instrumentSetupWasOpenRef = useRef(false);
  const [connectGate, setConnectGate] = useState<MarketDataConnectGateState | null>(null);

  const resolveGateFromCachedSetup = useCallback(() => {
    if (!enabled || !brokerAccountCode.trim()) {
      setConnectGate(null);
      return null;
    }
    if (!setupData) {
      setConnectGate(null);
      return null;
    }
    const gate = marketDataOhlcGateFromSetupScan(setupData, symbol);
    setConnectGate(gate);
    return gate;
  }, [brokerAccountCode, enabled, setupData, symbol]);

  useEffect(() => {
    if (!enabled) {
      setConnectGate(null);
      return;
    }
    resolveGateFromCachedSetup();
  }, [enabled, resolveGateFromCachedSetup]);

  useEffect(() => {
    if (instrumentSetupWasOpenRef.current && !instrumentSetupOpen) {
      resolveGateFromCachedSetup();
    }
    instrumentSetupWasOpenRef.current = instrumentSetupOpen;
  }, [instrumentSetupOpen, resolveGateFromCachedSetup]);

  const checkBeforeRun = useCallback(async () => {
    return resolveGateFromCachedSetup();
  }, [resolveGateFromCachedSetup]);

  const openMarketDataSetup = useCallback(
    (focusSymbol?: string) => {
      const code = brokerAccountCode.trim();
      if (!code) return;
      void openSetup(code, {
        mandatory: false,
        forceOpen: true,
        focusSymbol:
          focusSymbol?.trim() ||
          connectGate?.symbol?.trim() ||
          symbol?.trim() ||
          undefined,
      });
    },
    [brokerAccountCode, connectGate?.symbol, openSetup, symbol]
  );

  return {
    connectGate,
    scanning: false,
    runBlocked: Boolean(connectGate),
    checkBeforeRun,
    openMarketDataSetup,
    setConnectGate,
  };
}
